import SupportPostSearchBar from 'components/body/SupportPostSearchBar'
import GoogleTagManagerAttributes from 'components/core/GoogleTagManagerAttributes'
import HeroWithCustomComponent from 'components/hero/HeroWithCustomComponent'
import Footer from 'components/layout/Footer'
import PreviewNotice from 'components/layout/PreviewNotice'
import AnyHeroWrapper from 'components/renderers/AnyHeroWrapper'
import HeaderContainer from 'components/renderers/HeaderContainer'
import Env from 'Env'
import Markdown from 'markdown-to-jsx'
import { TopPosterModel } from 'models/body/TopPosterModel'
import { SupportPostModel } from 'models/core/SupportPostModel'
import { SupportTopicModel } from 'models/core/SupportTopicModel'
import { MenuModel } from 'models/menu/MenuModel'
import { NextPage } from 'next'
import { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import Link from 'next/link'
import React, { Fragment } from 'react'
import { ApiPromise } from 'services/ApiPromise'
import { Converters } from 'services/Converters'
import { useRouter } from 'next/router'
import ErrorPage from 'next/error'

type SupportPostPath = {
  params: { slug: string }
}

type Props = {
  menu: MenuModel
  footer: MenuModel
  supportPost: SupportPostModel['data']
  topics: {
    data: SupportTopicModel['data'][]
  }
  posters: {
    data: TopPosterModel[]
  }
  isPreview: boolean
}

const SupportPost: NextPage<Props> = (props) => {
  const router = useRouter()
  if (!router.isFallback && !props.supportPost) {
    return <ErrorPage statusCode={404} />
  }

  return (
    <Fragment>
      <Head>
        <title>Support - {props.supportPost.attributes.title}</title>
      </Head>
      <NextSeo title={`Support - ${props.supportPost.attributes.title}`} />
      <GoogleTagManagerAttributes />
      {props.isPreview && <PreviewNotice />}

      <HeaderContainer posters={props.posters} menu={props.menu} />

      <AnyHeroWrapper componentClassNames={`inner-banner md:pb-[65px] md:text-2xl pt-[50px] pb-[60px] bg-inner-banner bg-center bg-no-repeat bg-cover text-center z-10 relative text-lg text-white font-lato`}>
        <HeroWithCustomComponent data={{ title: 'Have a question?' }}>
          <SupportPostSearchBar />
        </HeroWithCustomComponent>
      </AnyHeroWrapper>
      <div className="pt-24 container lg:flex gap-x-8">
        <div className="w-full lg:w-1/3">
          <h2 className="text-2xl font-semibold font-lato">Support Categories</h2>
          <ul className="mt-2">
            {props.topics.data.map((topic, index) => (
              <Link key={index} href={`/article-categories/${topic.attributes.slug}`}>
                <li className="border-b border-gray-300 text-lg font-lato py-2 text-gray-600 cursor-pointer hover:text-[rgb(249,100,0)]">{topic.attributes.title}</li>
              </Link>
            ))}
          </ul>
        </div>
        <div className="support-prose w-full">
          <h1 className="support-entry-title">{props.supportPost.attributes.title}</h1>
          <Markdown>{props.supportPost.attributes.content}</Markdown>
        </div>
      </div>
      <Footer data={props.footer} />
    </Fragment>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const convert = (input: any) => Converters.KeysConvert.ToLowerCase(input)
  const slug = context.params?.slug as string
  const isPreview = context.preview
  const menuAndFooter = ApiPromise.Layout.GetMainAndFooterMenu()
  const [menuPromise, footerPromise, postersPromise] = await Promise.all(menuAndFooter)
  const [menu, footer, posters] = Converters.KeysConvert.ConvertAllToLower([menuPromise.data, footerPromise.data, postersPromise.data])
  const post = await ApiPromise.Blog.SupportPostBySlug(slug, isPreview).then((resp) => convert(resp.data))
  const topics = await ApiPromise.Blog.SupportTopics().then((resp) => convert(resp.data))
  const supportPost = post.data

  return { props: { menu, footer, supportPost: supportPost, topics: topics, posters: posters, isPreview: isPreview ? true : false }, revalidate: Env.PropsRevalidateTime }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const convert = (input: any) => Converters.KeysConvert.ToLowerCase(input)
  const posts = await ApiPromise.Blog.SupportPosts().then((resp) => convert(resp.data))
  const blogPosts: SupportPostModel['data'][] = posts['data']
  const pathsRecord: SupportPostPath[] = []
  blogPosts.map((post) => {
    pathsRecord.push({ params: { slug: post.attributes.slug } })
  })

  return { paths: pathsRecord, fallback: false }
}

export default SupportPost
